import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api.js';

const C_APPEARANCE_SETTINGS_KEY = 'oAppearanceSettings';
const C_DEFAULT_PAGE_TITLE = 'Acredite-se';

/**
 * Expõe informações necessárias para permitir uso sem o context.
 */
export const oConfigWL = {
  cPageTitle: '',
};

const defaultAppearance = {
  oConfig: null,
  getBrandImgSrc: () => "",
  getTurnstileKey: () => ""
};
const AppearanceContext = createContext(defaultAppearance);

const AppearanceProvider = ({ children }) => {
  const [oConfig, setConfig] = useState(null);

  const fetchAppearanceConfig = useCallback(async () => {
    try {
      const { data: oResult } = await api.get('/domain-identify');
      // renovar a cada 24 horas
      oResult.nExpiresAt = Date.now() + 24 * 3600 * 1000;
      localStorage.setItem(C_APPEARANCE_SETTINGS_KEY, JSON.stringify(oResult));
      return oResult;
    } catch (oError) {
      return {};
    }
  }, []);

  const getAppearanceConfig = useCallback(async () => {
    const oResult = JSON.parse(localStorage.getItem(C_APPEARANCE_SETTINGS_KEY));
    if (oResult != null && Date.now() < oResult.nExpiresAt) {
      return oResult;
    }
    return await fetchAppearanceConfig();
  }, [fetchAppearanceConfig]);

  const updateAppearanceConfig = useCallback(async () => {
    const oResult = await getAppearanceConfig();
    setConfig(oResult);
  }, [getAppearanceConfig]);

  /**
   * Sincroniza a config global com os dados mais atuais
   */
  const updateGlobalWLConfig = useCallback((oNewConfig) => {
    Object.assign(oConfigWL, oNewConfig);
    oConfigWL.cPageTitle = oNewConfig.cPageTitle ?? C_DEFAULT_PAGE_TITLE;
  }, []);

  useEffect(() => {
    updateAppearanceConfig();
  }, [updateAppearanceConfig]);

  useEffect(() => {
    if (oConfig == null) return;
    // NOTA: Certifique-se de que a config global será sincronizada corretamente ao alterar essa função
    updateGlobalWLConfig(oConfig);

    document.title = oConfigWL.cPageTitle;
    const elFavIcon = document.querySelector('link[rel="shortcut icon"]');
    if (elFavIcon) {
      elFavIcon.href = oConfig.cCdnBaseUrl
        ? getBrandImgSrc('favicon.ico')
        : process.env.PUBLIC_URL + '/favicon.ico';
    }
  }, [oConfig]);

  const isWL = useMemo(() => !oConfig || !!oConfig.nWlpnId, [oConfig]);

  /**
   * Retorna o caminho a ser utilizado para uma dada logo.
   * @param {string} cFileName nome esperado do arquivo da logo, com extensão. Exemplo: `logo-text-134x75.png`
   * @return {string} caminho ou url da imagem solicitada
   */
  const getBrandImgSrc = useCallback((cFileName) => {
    if (oConfig == null) return ''; // ainda não foi carregado
    const { nWlpnId, cCdnBaseUrl } = oConfig;
    if (!nWlpnId) {
      // string precisa ser hardcoded nesse require, se tentar usar uma constante, quebra
      return require(`../assets/img/logos/${cFileName}`);
    }
    return cCdnBaseUrl + `${nWlpnId}/${cFileName}`;
  }, [oConfig]);

  const getTurnstileKey = useCallback(() => {
    if (oConfig == null) return ''; // ainda não foi carregado
    if (oConfig.nWlpnId == null) {
      return process.env.REACT_APP_SITE_KEY_CAPTCHAL;
    }
    return oConfig.cTurnstileKey;
  }, [oConfig]);

  const oValues = useMemo(() => ({
    oConfig,
    isWL,
    getBrandImgSrc,
    getTurnstileKey,
  }), [oConfig, isWL, getBrandImgSrc, getTurnstileKey]);

  return (
    <AppearanceContext.Provider value={oValues}>
      {children}
    </AppearanceContext.Provider>
  );
};

// Componentes que utilizam o contexto para configurar exibição de acordo com o domínio

/**
 * @param {React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & { cFileName: string, bHasTitleAlt?: boolean}} props
 * @param {string} props.cFileName nome do arquivo da logo na CDN ou na pasta de assets, com extensão
 *                                 > Exemplo: `logo-text-134x75.png`
 * @param {boolean} [props.bHasTitleAlt] se deve incluir atributo alt com o título do sistema
 */
const BrandImage = ({ cFileName, bHasTitleAlt = false, alt, logo, ...props }) => {
  return (
    <AppearanceContext.Consumer>
      {({ getBrandImgSrc, oConfig }) => {
        return (
          <img
            src={logo || getBrandImgSrc(cFileName)}
            alt={(bHasTitleAlt && oConfig != null)
              ? oConfig.cPageTitle ?? C_DEFAULT_PAGE_TITLE
              : alt}
            {...props}
          />
        );
      }}
    </AppearanceContext.Consumer>
  )
};

/**
 * Título a ser usado pelo sistema
 */
const BrandTitle = () => {
  return (
    <AppearanceContext.Consumer>
      {({ oConfig }) => {
        if (oConfig == null) {
          return ''; // ainda não foi carregado
        }
        return oConfig.cPageTitle ?? C_DEFAULT_PAGE_TITLE;
      }}
    </AppearanceContext.Consumer>
  )
};

const useAppearanceContext = () => useContext(AppearanceContext);

export { AppearanceProvider, BrandImage, BrandTitle, useAppearanceContext };
